import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ArrowRight, ExternalLink, Quote } from 'lucide-react';

const ANALYSTS = [
  {
    date: 'AUGUST 18, 2026',
    name: 'A stablecoin compliance analyst',
    outlet: 'A digital-money podcast',
    accent: 'rose',
    piece: 'The Agentic Economy · Episode 1',
    frame: 'Seven Conditions before software can spend your money.',
    verdict: 'Three of seven hold. Four do not.',
    quote: 'A control you cannot demonstrate you can stop is not a control.',
    detail: 'A stablecoin compliance analyst scored the market at three of seven. Conditions three, five, six, and seven remain open. Condition six, recourse when the agent turns out to be wrong, is the one no vendor currently answers.',
  },
  {
    date: 'AUGUST 21, 2026',
    name: 'A payments research writer',
    outlet: 'An industry research newsletter',
    accent: 'cyan',
    piece: 'The Authorization and Identity Layer, Four Ways',
    frame: 'Four August announcements. All in the same layer.',
    verdict: 'None of them touched recourse.',
    quote: 'Which of your systems, payments or fraud, should handle it, is still unresolved.',
    detail: 'A card-issuing startup launched the APA; a global cloud hyperscaler made its agent payments product generally available; a major credit bureau shipped Agent Trust; a global super-app wallet launched AHA. Four positions of the same layer. Not one addresses what happens when the transaction settled and the outcome was still wrong.',
  },
  {
    date: 'AUGUST 22, 2026',
    name: 'Senior payments-industry analyst (25+ yrs)',
    outlet: 'A payments trade publication',
    accent: 'amber',
    piece: 'On the agentic payments industry alliance',
    frame: 'Four operational categories every platform must solve.',
    verdict: 'Recourse is the one no one is building.',
    quote: 'Not in the demo. In the dispute.',
    detail: 'A senior payments-industry analyst with 25 years of platform-building named the four categories directly: Mandate. Evidence. Recourse. Reconciliation. He then observed that the Alliance is a liability negotiation, not an innovation club, and that recourse is the one downstream of both mandate and reconciliation.',
  },
  {
    date: 'FEBRUARY 18, 2026',
    name: 'A former global-wallet product leader',
    outlet: 'Social · Fractional CPO (formerly at a global wallet and several SaaS platforms)',
    accent: 'emerald',
    piece: 'Why should anyone trust the agent?',
    frame: 'A trust chain: Human → Agent → Intent → Authority → Transaction → Outcome.',
    verdict: 'The winner may not be the company that owns the payment. It may be the company that owns the trust decision around the payment.',
    quote: 'The biggest opportunity in agentic commerce may not be making agents more autonomous. It may be making them more accountable.',
    detail: 'The product leader, formerly a product leader at a global wallet, described the missing layer as a full chain of verifiable claims. Then a senior payments-industry analyst (25+ years) replied under the post: &ldquo;A verifiable claim only matters if some party is obligated to reject the transaction that arrives without one, and today nobody is. If the agent does not hold a usable credential until the authority exists, bypass stops being a policy violation and becomes impossible. Owning the trust decision is a business. Owning the mint is a moat.&rdquo; That comment specifies the shape of the instrument, and it maps directly to our <a href="/kypa/instrument" class="text-emerald-300 underline">KYPA Credential Envelope</a>.',
  },
];

const AccentDot = ({ accent }) => {
  const map = { rose: 'bg-rose-500', cyan: 'bg-cyan-500', amber: 'bg-amber-500', emerald: 'bg-emerald-500' };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[accent]}`} />;
};

const AccentBorder = ({ accent, children }) => {
  const map = {
    rose: 'border-rose-500/50',
    cyan: 'border-cyan-500/50',
    amber: 'border-amber-500/50',
    emerald: 'border-emerald-500/50',
  };
  return <div className={`border-l-2 ${map[accent]} pl-6 sm:pl-10`}>{children}</div>;
};

const AccentText = ({ accent, children, className = '' }) => {
  const map = { rose: 'text-rose-300', cyan: 'text-cyan-300', amber: 'text-amber-300', emerald: 'text-emerald-300' };
  return <span className={`${map[accent]} ${className}`}>{children}</span>;
};

export default function ThreeAnalystsOneGap() {
  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Four Analysts. One Gap. Same Story. · Kojiru</title>
        <meta
          name="description"
          content="A stablecoin compliance analyst, a payments research writer, a senior payments-industry analyst, and a former global-wallet product leader. Four vantages, four frameworks, one conclusion: the recourse layer is where the next two years of agentic commerce will be decided, and the moat is owning the mint."
        />
        <meta property="og:title" content="Four Analysts. One Gap. Same Story." />
        <meta property="og:description" content="What emerges when you overlay four of the sharpest agentic-commerce essays." />
      </Helmet>

      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-6">
              ESSAY &middot; PUBLIC &middot; UPDATED FEB 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6">
              Four Analysts. One Gap. Same Story.
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-3xl mb-4">
              Across six months, four independent analysts published pieces about the future
              of agentic commerce. None of them coordinated. None of them named Kojiru. None of them named
              each other. And then, on 18 February 2026, a former a global wallet / a P2P wallet product leader
              posted the summary of all four in a single 500-word frame, and a 25-year payments
              executive commented under it with the exact shape of the instrument that closes it.
            </p>
            <p className="text-slate-400 leading-relaxed max-w-3xl">
              Read the four side by side and something specific happens: the same shape appears in four
              different vocabularies. This essay is what that shape looks like, and why we think it matters.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The three, at-a-glance */}
      <section className="py-16 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">THE FOUR PIECES</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-8 leading-tight">
            Four pieces in six months.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {ANALYSTS.map((a) => (
              <motion.div
                key={a.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-[#0f0f16] border border-white/10 rounded-xl p-6"
                data-testid={`analyst-card-${a.name.split(' ')[0].toLowerCase()}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <AccentDot accent={a.accent} />
                  <div className="text-[10px] font-bold tracking-[1.5px] text-slate-500">{a.date}</div>
                </div>
                <div className="text-lg font-bold text-white mb-1">{a.name}</div>
                <div className="text-xs text-slate-500 italic mb-4">{a.outlet}</div>
                <p className="text-sm text-slate-300 leading-relaxed mb-3">{a.frame}</p>
                <p className="text-xs font-semibold">
                  <AccentText accent={a.accent}>{a.verdict}</AccentText>
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep dives */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-16">
          {ANALYSTS.map((a) => (
            <motion.div
              key={a.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5 }}
              data-testid={`analyst-detail-${a.name.split(' ')[0].toLowerCase()}`}
            >
              <AccentBorder accent={a.accent}>
                <div className="text-[10px] font-bold tracking-[2px] text-slate-500 mb-2">{a.date}</div>
                <h3 className="text-2xl sm:text-3xl font-black mb-1 leading-tight">{a.name}</h3>
                <div className="text-sm text-slate-500 italic mb-6">{a.outlet} &middot; {a.piece}</div>

                <div className="flex gap-3 mb-6">
                  <Quote className={`w-5 h-5 flex-shrink-0 mt-1 ${a.accent === 'rose' ? 'text-rose-400' : a.accent === 'cyan' ? 'text-cyan-400' : 'text-amber-400'}`} />
                  <p className="text-xl sm:text-2xl italic text-white leading-snug">&ldquo;{a.quote}&rdquo;</p>
                </div>

                <p className="text-slate-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: a.detail }} />
              </AccentBorder>
            </motion.div>
          ))}
        </div>
      </section>

      {/* The overlay */}
      <section className="py-20 px-6 border-t border-white/5 bg-gradient-to-b from-white/[0.02] to-rose-950/10">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-cyan-400 mb-3">THE OVERLAY</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6 leading-tight">
            What emerges when you lay the four on top of each other.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            A stablecoin compliance analyst, a payments research writer, a senior payments-industry analyst, and a former global-wallet product leader write for four completely different
            audiences. The compliance analyst writes for compliance officers and stablecoin professionals. The research writer writes
            for business leaders considering AI adoption. The payments-industry analyst writes for payments platform operators
            with real disputed transactions on their books. The product leader writes for product leaders at
            fintech and B2B SaaS companies who monetize trust decisions.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            None of them read one another for their pieces. Yet if you take the four
            frameworks and lay them on top of each other, they name the same missing piece: in
            four different vocabularies:
          </p>
          <div className="bg-[#0f0f16] border border-white/10 rounded-xl p-6 my-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold tracking-[1.5px] text-slate-500 text-left">
                  <th className="pb-3">ANALYST</th>
                  <th className="pb-3">CALLS IT</th>
                  <th className="pb-3">SAYS IT LIKE THIS</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                <tr className="border-t border-white/5">
                  <td className="py-3 font-semibold text-white">A stablecoin compliance analyst</td>
                  <td className="py-3"><AccentText accent="rose">Condition 6</AccentText></td>
                  <td className="py-3 italic">&ldquo;There is recourse when it goes wrong.&rdquo;</td>
                </tr>
                <tr className="border-t border-white/5">
                  <td className="py-3 font-semibold text-white">A payments research writer</td>
                  <td className="py-3"><AccentText accent="cyan">The third system</AccentText></td>
                  <td className="py-3 italic">&ldquo;Neither payments nor fraud.&rdquo;</td>
                </tr>
                <tr className="border-t border-white/5">
                  <td className="py-3 font-semibold text-white">Senior payments analyst (25+ yrs)</td>
                  <td className="py-3"><AccentText accent="amber">Recourse</AccentText></td>
                  <td className="py-3 italic">&ldquo;Not in the demo. In the dispute.&rdquo;</td>
                </tr>
                <tr className="border-t border-white/5">
                  <td className="py-3 font-semibold text-white">A former global-wallet product leader</td>
                  <td className="py-3"><AccentText accent="emerald">The trust layer</AccentText></td>
                  <td className="py-3 italic">&ldquo;Owning the trust decision, not the payment.&rdquo;</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-slate-300 leading-relaxed mb-4">
            Same layer. Same absence. Same conclusion: the market has largely solved authorization and
            identity. What it has not solved is what happens when a properly authorized and correctly
            identified agent still delivers the wrong outcome, and how to make an agent that
            hasn&rsquo;t proven accountability incapable of transacting in the first place.
          </p>
          <p className="text-slate-300 leading-relaxed mb-4">
            The payments-industry analyst&rsquo;s comment under the product leader&rsquo;s post added the shape of the fix:{' '}
            <em>&ldquo;If the agent does not hold a usable credential until the authority exists,
            bypass stops being a policy violation and becomes impossible. Owning the trust decision is
            a business. Owning the mint is a moat.&rdquo;</em>
          </p>
          <p className="text-slate-300 leading-relaxed">
            That is what the <Link to="/kypa/instrument" className="text-emerald-300 underline hover:text-emerald-200">KYPA Credential Envelope</Link> ships.
            The layer everyone points at, and the one thing that finally reaches for the mint.
          </p>
        </div>
      </section>

      {/* What Kojiru is */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-bold tracking-[2px] text-rose-400 mb-3">WHAT WE ARE</div>
          <h2 className="text-3xl sm:text-4xl font-black mb-6 leading-tight">
            The recourse layer, shipped.
          </h2>
          <p className="text-slate-300 leading-relaxed mb-4">
            Kojiru is a public, non-custodial registry of AI agents that have pledged USDC on Base as
            a public commitment signal. When a KYPA-registered agent breaches a promise it published, the
            breach is recorded publicly next to its pledge. The pledge is a public commitment signal,
            not insurance and not a promise of repayment. Kojiru never holds either party&rsquo;s funds.
          </p>
          <p className="text-slate-300 leading-relaxed mb-6">
            Everything else about Kojiru is downstream of that one mechanic.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/kypa">
              <Button size="lg" className="bg-white text-black hover:bg-slate-100" data-testid="cta-kypa">
                See how KYPA works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/seven-conditions">
              <Button size="lg" variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="cta-seven-conditions">
                Read the Seven Conditions response
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Close */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto mb-4">
            If any framing in this essay overreaches, tell us where. If any of the three analysts should be
            more precisely represented, tell us how. We would rather hear it before anyone else reads.
          </p>
          <p className="text-slate-500 text-sm">
            wayne@kojiru.com &middot; kojiru.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
